import * as React from "react";

import {
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button, type ButtonProps } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { toastManager } from "../../components/ui/toast";
import type { Omittable } from "../../lib/props";

import type {
  BulkSelectionNoun,
  BulkAction,
  BulkActionGroup,
  BulkActionConfirm,
  BulkActionSuccessMessage,
  BulkActionUndo,
  SelectAllGroup,
} from "./bulk-actions-bar-types";

// The pieces every bulk-action presentation shares. The grammar renders three
// ways — the desktop strip, the mobile sheet, and the More-actions command
// dialog — and all three execute actions through the one flow here (loading,
// toast, clear selection on success). The strip and the dialog live in
// sibling files, so the shared pieces live in neither.

/**
 * Tooltips on the strip float higher than the default 4px: the controls sit
 * 8px inside the strip, so a bubble has to travel that inset plus a 4px gap
 * to clear the strip's top edge — landing on the edge reads as stuck to it.
 */
export const BAR_TOOLTIP_OFFSET = 12;

/** The state and callbacks every per-action control receives. */
export type ActionButtonSharedProps<TData> = {
  selectedRows: TData[];
  displayCount: number;
  /** The count chip's noun; omitted = "N selected". */
  selectionNoun?: BulkSelectionNoun | undefined;
  selectAllGroups: SelectAllGroup[];
  loadingActionId: string | null;
  onClearSelection: () => void;
  setLoadingActionId: (id: string | null) => void;
};

/**
 * Whether the strip draws an action as its icon alone (label on the tooltip).
 * Every destructive action is — the remove/delete verbs sit at the strip's
 * end as glyphs, the reference-bar grammar — plus any action that asks for
 * it. Needs an icon to draw; a destructive action without one keeps its word.
 */
export function isStripIconOnly<TData>(action: BulkAction<TData>): boolean {
  return (action.iconOnly === true || action.destructive === true) && action.icon != null;
}

/**
 * The More-dialog heading an action lists under: its declared `group`, else
 * one derived from its type — picking a value sets, running a verb applies.
 */
export function bulkActionGroupOf<TData>(
  action: BulkAction<TData>,
): BulkActionGroup {
  if (action.group != null) return action.group;
  return action.type === "popover" || action.type === "option-picker"
    ? "set"
    : "apply";
}

/** "3 Projects selected" — the one count-chip sentence the strip and the
 *  More dialog both print. */
export function selectionCountLabel(
  count: number,
  noun: BulkSelectionNoun | undefined,
): string {
  const word = noun ? ` ${count === 1 ? noun.one : noun.other}` : "";
  return `${count.toLocaleString()}${word} selected`;
}

export function ActionIcon({
  icon,
  size = "sm",
  color,
  className,
}: Omittable<{
  icon: BulkAction<unknown>["icon"];
  size?: "sm" | "xs";
  color?: "muted";
  className?: string;
}>): React.ReactElement | null {
  if (!icon) return null;
  return typeof icon === "string" ? (
    <Icon className={className} color={color} name={icon} size={size} />
  ) : (
    <Icon as={icon} className={className} color={color} size={size} />
  );
}

// ---------------------------------------------------------------------------
// Action execution
// ---------------------------------------------------------------------------

type ExecuteBulkActionOptions = Omittable<{
  actionId: string;
  actionFn: () => Promise<BulkActionUndo | void> | BulkActionUndo | void;
  label: string;
  count: number;
  onClearSelection: () => void;
  setLoadingActionId: (id: string | null) => void;
  // An action with nothing to announce leaves it out, and a caller threading
  // through an optional message means the same thing.
  successMessage?: React.ReactNode | undefined;
  /** A `quiet` direct action: no success toast, selection kept. */
  quiet?: boolean;
}>;

export function resolveSuccessMessage<TData>(
  message: BulkActionSuccessMessage<TData> | undefined,
  selectedRows: TData[],
  count: number,
  optionId?: string,
): React.ReactNode {
  return typeof message === "function"
    ? message(selectedRows, count, optionId)
    : message;
}

export function resolveConfirmText<TData>(
  value: string | ((rows: TData[]) => string),
  rows: TData[],
): string {
  return typeof value === "function" ? value(rows) : value;
}

export async function executeBulkAction({
  actionId,
  actionFn,
  label,
  count,
  onClearSelection,
  setLoadingActionId,
  successMessage,
  quiet,
}: ExecuteBulkActionOptions): Promise<void> {
  setLoadingActionId(actionId);
  let succeeded = false;
  let undo: BulkActionUndo | void = undefined;
  try {
    undo = await actionFn();
    succeeded = true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Unknown error";
    toastManager.add({ title: `${label} failed: ${message}`, type: "error" });
  } finally {
    setLoadingActionId(null);
  }
  if (succeeded && !quiet) {
    const title =
      successMessage ??
      `${label} applied to ${count.toLocaleString()} item${count !== 1 ? "s" : ""}`;
    if (undo) {
      const { onUndo, shortcut, timeout } = undo;
      // An undo offer is a neutral toast (no status icon), like the
      // single-item delete toast. Toast.Action does not close its toast, so
      // the press closes it.
      const toastId = toastManager.add({
        title,
        actionProps: {
          children: "Undo",
          onClick: () => {
            onUndo();
            toastManager.close(toastId);
          },
        },
        ...(shortcut ? { data: { actionShortcut: shortcut } } : {}),
        ...(timeout !== undefined ? { timeout } : {}),
      });
    } else {
      toastManager.add({ title, type: "success" });
    }
    onClearSelection();
  }
}

// ---------------------------------------------------------------------------
// Bar action button
// The bar is a toolbar strip, so its actions are quiet ghost buttons on the
// 32px rung, squared: a control acting on the strip takes the strip's
// rectangle, not the free-standing stadium. Destructive actions keep the same
// geometry in the danger hue.
// ---------------------------------------------------------------------------

export type BarButtonProps = ButtonProps & Omittable<{ destructive?: boolean }>;

export function BarButton({
  destructive,
  ...props
}: BarButtonProps): React.ReactElement {
  return (
    <Button
      shape="square"
      color={destructive ? "destructive" : "neutral"}
      variant="ghost"
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Confirm popup
// The one confirmation body, mounted under an AlertDialog root by both the
// strip's confirm button and the More-actions dialog (which nests it over the
// command popup — the nested-dialog recession keeps the parent visible).
// ---------------------------------------------------------------------------

export function ConfirmActionPopup<TData>({
  action,
  selectedRows,
  onConfirm,
}: {
  action: BulkActionConfirm<TData>;
  selectedRows: TData[];
  onConfirm: () => void;
}): React.ReactElement {
  const title = resolveConfirmText(action.confirmTitle, selectedRows);
  const description = resolveConfirmText(
    action.confirmDescription,
    selectedRows,
  );
  return (
    <AlertDialogPopup>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogClose render={<Button variant="outline" />}>
          Cancel
        </AlertDialogClose>
        <Button
          onClick={onConfirm}
          color={action.destructive ? "destructive" : "primary"}
        >
          {action.confirmLabel ?? action.label}
        </Button>
      </AlertDialogFooter>
    </AlertDialogPopup>
  );
}
